import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../components/constants';

// Async thunks for API calls
export const fetchAllSubscriptions = createAsyncThunk(
  'subscriptions/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/subscriptions`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch subscriptions');
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscriptions/create',
  async (subscriptionData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/subscriptions`, subscriptionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create subscription');
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscriptions/update',
  async ({ subscription_id, ...subscriptionData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/subscriptions`, { subscription_id, ...subscriptionData });
      return { ...response.data, subscription_id };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update subscription');
    }
  }
);

// Grant temporary credits without changing max_runs
export const grantTemporaryCredit = createAsyncThunk(
  'subscriptions/grantTemporaryCredit',
  async ({ subscription_id, task_id, credits, pages }, { rejectWithValue }) => {
    try {
      const payload = {
        subscription_id,
        task_id,
        credits: Number(credits)
      };
      if (pages !== undefined && pages !== null && pages !== '') {
        payload.pages = Number(pages);
      }
      const response = await axios.post(`${BASE_URL}/subscriptions/temporary-credit`, payload);
      return { subscription_id, task_id, credits: Number(credits), pages: payload.pages };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to grant temporary credit');
    }
  }
);

export const deleteSubscription = createAsyncThunk(
  'subscriptions/delete',
  async (subscription_id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/subscriptions/${subscription_id}`);
      return subscription_id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete subscription');
    }
  }
);

// Admin/testing: Rewind created_at for SubscriptionTaskLimits by one month
export const rewindSubscriptionTaskLimitsDates = createAsyncThunk(
  'subscriptions/rewindDates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/subscriptions/rewind-dates`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to rewind dates');
    }
  }
);

// New function to get tasks by organization
export const fetchTasksByOrganization = createAsyncThunk(
  'subscriptions/fetchTasksByOrganization',
  async (organization_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/organizations/${organization_id}/tasks`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tasks by organization');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscriptions',
  initialState: {
    subscriptions: [],
    loading: false,
    error: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    organizationTasks: [],
    organizationTasksLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOrganizationTasks: (state) => {
      state.organizationTasks = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all subscriptions
      .addCase(fetchAllSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchAllSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create subscription
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the subscriptions list instead of manually adding
        state.status = 'idle';
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update subscription
      .addCase(updateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the subscriptions list instead of manually updating
        state.status = 'idle';
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete subscription
      .addCase(deleteSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubscription.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the subscriptions list instead of manually filtering
        state.status = 'idle';
      })
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Grant temporary credits
      .addCase(grantTemporaryCredit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(grantTemporaryCredit.fulfilled, (state) => {
        state.loading = false;
        state.status = 'idle';
      })
      .addCase(grantTemporaryCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Rewind dates (admin/testing)
      .addCase(rewindSubscriptionTaskLimitsDates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rewindSubscriptionTaskLimitsDates.fulfilled, (state) => {
        state.loading = false;
        state.status = 'idle';
      })
      .addCase(rewindSubscriptionTaskLimitsDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch tasks by organization
      .addCase(fetchTasksByOrganization.pending, (state) => {
        state.organizationTasksLoading = true;
        state.error = null;
      })
      .addCase(fetchTasksByOrganization.fulfilled, (state, action) => {
        state.organizationTasksLoading = false;
        state.organizationTasks = action.payload.tasks || [];
      })
      .addCase(fetchTasksByOrganization.rejected, (state, action) => {
        state.organizationTasksLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearOrganizationTasks } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;